"use client";

import type React from "react";

import { useState, useEffect } from "react";
import styles from "./ConfigModal.module.css";
import { FiX, FiPlus, FiCheck } from "react-icons/fi";

interface IConfigModalProps {
  name: string;
  treatmentConfig: Record<string, string>;
  onClose: () => void;
  handleTreatmentChange: (updatedConfig: Record<string, string>) => void;
}

const ConfigModal = (props: IConfigModalProps) => {
  const { name, treatmentConfig, onClose, handleTreatmentChange } = props;

  const [config, setConfig] = useState<Record<string, string>>(
    treatmentConfig || {}
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);

  // Extract logic for checking configuration changes
  const checkForChanges = () => {
    const originalKeys = Object.keys(treatmentConfig || {}).sort();
    const currentKeys = Object.keys(config).sort();

    if (originalKeys.length !== currentKeys.length) {
      return true;
    }

    for (let i = 0; i < originalKeys.length; i++) {
      if (
        originalKeys[i] !== currentKeys[i] ||
        treatmentConfig[originalKeys[i]] !== config[currentKeys[i]]
      ) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    const changesDetected = checkForChanges();
    setHasChanges(changesDetected);
  }, [config, treatmentConfig]);

  const handleConfigurationChange = (key: string, value: string) => {
    setConfig((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleRemoveConfiguration = (key: string) => {
    const newConfig = { ...config };
    delete newConfig[key];
    setConfig(newConfig);
  };

  const handleAddConfiguration = () => {
    const configCount = Object.keys(config).length;
    const newKey = `property_${configCount + 1}`;
    setConfig((prev) => ({
      ...prev,
      [newKey]: "",
    }));
  };

  const handleDone = () => {
    handleTreatmentChange(config);
    setShowSaveConfirmation(true);

    setTimeout(() => {
      setShowSaveConfirmation(false);
      onClose();
    }, 2000);
  };

  const handleConfigInputChange =
    (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      handleConfigurationChange(key, e.target.value);
    };

  const handleRemoveConfigClick = (key: string) => () => {
    handleRemoveConfiguration(key);
  };

  // Extract complex conditions
  const configCount = Object.keys(config).length;
  const hasEmptyValues = Object.values(config).some((v) => v === "");
  const saveButtonText = hasChanges ? "Save Changes" : "Done";
  const saveButtonClass = `${styles.saveButton} ${
    hasChanges ? styles.hasChanges : ""
  }`;

  // Render empty configuration message
  if (configCount === 0) {
    return (
      <div className={styles.modal}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <h3>Treatment Configuration: {name}</h3>
            <div className={styles.headerActions}>
              {showSaveConfirmation && (
                <div className={styles.saveConfirmation}>
                  <FiCheck className={styles.checkIcon} /> Saved
                </div>
              )}
              <button className={styles.closeButton} onClick={onClose}>
                <FiX />
              </button>
            </div>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.configSummary}>
              <p className={styles.emptyConfig}>No configurations added yet</p>
            </div>
            <div className={styles.configList}></div>
            <button
              className={styles.addButton}
              onClick={handleAddConfiguration}
            >
              <FiPlus /> Add Configuration
            </button>
          </div>
          <div className={styles.modalFooter}>
            <button
              className={saveButtonClass}
              onClick={handleDone}
              disabled={hasEmptyValues}
            >
              {saveButtonText}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3>Treatment Configuration: {name}</h3>
          <div className={styles.headerActions}>
            {showSaveConfirmation && (
              <div className={styles.saveConfirmation}>
                <FiCheck className={styles.checkIcon} /> Saved
              </div>
            )}
            <button className={styles.closeButton} onClick={onClose}>
              <FiX />
            </button>
          </div>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.configSummary}>
            <p>
              {configCount} configuration{configCount !== 1 ? "s" : ""}
            </p>
          </div>
          <div className={styles.configList}>
            {Object.entries(config).map(([key, value]) => (
              <div key={key} className={styles.configItem}>
                <input
                  type="text"
                  value={key}
                  className={styles.configKey}
                  readOnly
                />
                <input
                  type="text"
                  value={value}
                  onChange={handleConfigInputChange(key)}
                  className={styles.configValue}
                  placeholder="Enter value"
                />
                <button
                  className={styles.removeButton}
                  onClick={handleRemoveConfigClick(key)}
                >
                  <FiX />
                </button>
              </div>
            ))}
          </div>
          <button className={styles.addButton} onClick={handleAddConfiguration}>
            <FiPlus /> Add Configuration
          </button>
        </div>

        <div className={styles.modalFooter}>
          <button
            className={saveButtonClass}
            onClick={handleDone}
            disabled={hasEmptyValues}
          >
            {saveButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigModal;
